import {
  contributionHistorySchema,
  executionDashboardSchema,
  masterDashboardSchema,
  personalPositionSchema,
  type ContributionKind,
} from '@mfos/shared';
import { useQuery } from '@tanstack/react-query';
import { apiGet, withQuery } from '../../lib/api-client';
import { workspaceKey, workspacePath } from '../workspace/workspace.api';

export const reportsKey = (workspaceId: string) =>
  [...workspaceKey(workspaceId), 'reports'] as const;

export interface ReportWindow {
  readonly from?: string;
  readonly to?: string;
}

export function useExecutionDashboard(workspaceId: string, window: ReportWindow) {
  return useQuery({
    queryKey: [...reportsKey(workspaceId), 'execution', window] as const,
    queryFn: ({ signal }) =>
      apiGet(
        withQuery(workspacePath(workspaceId, 'reports', 'execution'), {
          from: window.from,
          to: window.to,
        }),
        { schema: executionDashboardSchema, signal },
      ),
  });
}

export interface ContributionFilters extends ReportWindow {
  readonly kind?: ContributionKind;
  readonly q?: string;
}

export function useContributions(
  workspaceId: string,
  founderId: string,
  filters: ContributionFilters,
  enabled = true,
) {
  return useQuery({
    queryKey: [...reportsKey(workspaceId), 'contributions', founderId, filters] as const,
    queryFn: ({ signal }) =>
      apiGet(
        withQuery(workspacePath(workspaceId, 'founders', founderId, 'contributions'), {
          from: filters.from,
          to: filters.to,
          kind: filters.kind,
          q: filters.q,
        }),
        { schema: contributionHistorySchema, signal },
      ),
    enabled,
  });
}

export function useMasterDashboard(workspaceId: string) {
  return useQuery({
    queryKey: [...reportsKey(workspaceId), 'master'] as const,
    queryFn: ({ signal }) =>
      apiGet(workspacePath(workspaceId, 'reports', 'master'), {
        schema: masterDashboardSchema,
        signal,
      }),
  });
}

export function usePersonalPosition(workspaceId: string, enabled = true) {
  return useQuery({
    queryKey: [...reportsKey(workspaceId), 'position'] as const,
    queryFn: ({ signal }) =>
      apiGet(workspacePath(workspaceId, 'me', 'position'), {
        schema: personalPositionSchema,
        signal,
      }),
    enabled,
  });
}
